import type { ICatalogAccess } from './catalog-access';
import type { TCatalogTier } from './catalog-layout';

/**
 * The entries of the Menú page's ⋯ menus (plan §5.7, §5.8, DESIGN §3.5):
 * the product row, the category card and «Más acciones». Pure, so every
 * role and tier combination is spec'd; the page maps keys to actions.
 */

export type TCatalogPlanBadge = 'plus' | 'pro';

export interface ICatalogMenuEntry<TKey extends string> {
  key: TKey;
  /** i18n key of the label. */
  labelKey: string;
  /** mdi icon name, without the `mdi-` prefix. */
  icon: string;
  /** Red (destructive). */
  danger: boolean;
  /** It saves something: disabled while offline. */
  write: boolean;
  /** The plan it needs when the store's tier is lower (the route leads to the plans). */
  plan: TCatalogPlanBadge | null;
}

function entry<TKey extends string>(
  key: TKey,
  labelKey: string,
  icon: string,
  options: {
    danger?: boolean;
    write?: boolean;
    plan?: TCatalogPlanBadge | null;
  } = {}
): ICatalogMenuEntry<TKey> {
  return {
    key,
    labelKey,
    icon,
    danger: options.danger ?? false,
    write: options.write ?? options.danger ?? false,
    plan: options.plan ?? null,
  };
}

//#region Product row ⋯

export type TRowMenuKey =
  | 'edit'
  | 'price'
  | 'duplicate'
  | 'move'
  | 'view'
  | 'delete';

/** «Editar», «Cambiar precio», «Duplicar», «Mover…», «Ver en la carta», «Eliminar». */
export function rowMenuEntries(
  access: ICatalogAccess,
  canViewInMenu: boolean
): ICatalogMenuEntry<TRowMenuKey>[] {
  const entries: ICatalogMenuEntry<TRowMenuKey>[] = [
    access.canManage
      ? entry('edit', 'menuPage.rowMenu.edit', 'pencil-outline')
      : entry('edit', 'menuPage.rowMenu.details', 'eye-outline'),
  ];
  if (access.canManage)
    entries.push(
      entry('price', 'menuPage.rowMenu.price', 'currency-usd', {
        write: true,
      })
    );
  // The copy is created hidden everywhere: only roles that manage every
  // branch can show it afterwards.
  if (access.canManage && access.canHideEverywhere)
    entries.push(
      entry('duplicate', 'menuPage.rowMenu.duplicate', 'content-copy', {
        write: true,
      })
    );
  if (access.canManage)
    entries.push(
      entry('move', 'menuPage.rowMenu.move', 'folder-move-outline', {
        write: true,
      })
    );
  if (canViewInMenu)
    entries.push(entry('view', 'menuPage.rowMenu.view', 'open-in-new'));
  if (access.canManage)
    entries.push(
      entry('delete', 'menuPage.rowMenu.delete', 'delete-outline', {
        danger: true,
      })
    );
  return entries;
}

//#endregion

//#region Category ⋯

export type TCategoryMenuKey =
  | 'edit'
  | 'addProduct'
  | 'hideProducts'
  | 'showProducts'
  | 'reorder'
  | 'select'
  | 'delete';

export interface ICategoryMenuInput {
  access: ICatalogAccess;
  /** The category's live products. */
  itemCount: number;
  /** How many of them are visible (the `visible` flag). */
  visibleCount: number;
}

/**
 * «Editar categoría», «Agregar producto aquí», «Ocultar / Mostrar todos sus
 * productos» (both when mixed), «Ordenar productos», «Seleccionar
 * productos», «Eliminar categoría».
 */
export function categoryMenuEntries(
  input: ICategoryMenuInput
): ICatalogMenuEntry<TCategoryMenuKey>[] {
  const { access, itemCount, visibleCount } = input;
  const entries: ICatalogMenuEntry<TCategoryMenuKey>[] = [
    access.canManage
      ? entry('edit', 'menuPage.categoryMenu.edit', 'pencil-outline')
      : entry('edit', 'menuPage.categoryMenu.details', 'eye-outline'),
  ];
  if (access.canManage)
    entries.push(
      entry('addProduct', 'menuPage.categoryMenu.addProduct', 'plus')
    );
  if (access.canHideEverywhere && itemCount > 0) {
    if (visibleCount > 0)
      entries.push(
        entry(
          'hideProducts',
          'menuPage.categoryMenu.hideProducts',
          'eye-off-outline',
          { write: true }
        )
      );
    if (visibleCount < itemCount)
      entries.push(
        entry(
          'showProducts',
          'menuPage.categoryMenu.showProducts',
          'eye-outline',
          { write: true }
        )
      );
  }
  if (access.canManage && itemCount > 1)
    entries.push(
      entry('reorder', 'menuPage.categoryMenu.reorder', 'swap-vertical')
    );
  if (access.canToggle && itemCount > 0)
    entries.push(
      entry(
        'select',
        'menuPage.categoryMenu.select',
        'checkbox-multiple-marked-outline'
      )
    );
  if (access.canManage)
    entries.push(
      entry('delete', 'menuPage.categoryMenu.delete', 'delete-outline', {
        danger: true,
      })
    );
  return entries;
}

//#endregion

//#region «Más acciones»

export type TMoreMenuKey =
  | 'newCategory'
  | 'reorder'
  | 'select'
  | 'options'
  | 'import'
  | 'bulkPrices'
  | 'stickers'
  | 'settings'
  | 'viewMenu';

export interface IMoreMenuInput {
  access: ICatalogAccess;
  tier: TCatalogTier;
  /** The store's effective plan tier (Plus = 1, Pro = 2). */
  planTier: number;
  multipleMenus: boolean;
  /** The current menu has products (selection needs some). */
  hasProducts: boolean;
  /** Two categories, or a category with two products. */
  canReorder: boolean;
  /** The store has a public link. */
  canViewMenu: boolean;
}

function planBadge(
  planTier: number,
  minTier: number
): TCatalogPlanBadge | null {
  if (planTier >= minTier) return null;
  return minTier === 1 ? 'plus' : 'pro';
}

/** The page's ⋯ (plan §5.7), entries hidden by access. */
export function moreMenuEntries(
  input: IMoreMenuInput
): ICatalogMenuEntry<TMoreMenuKey>[] {
  const { access, tier } = input;
  const entries: ICatalogMenuEntry<TMoreMenuKey>[] = [];
  if (access.canManage)
    entries.push(
      entry('newCategory', 'menuPage.more.newCategory', 'folder-plus-outline')
    );
  if (access.canManage && input.canReorder)
    entries.push(entry('reorder', 'menuPage.more.reorder', 'swap-vertical'));
  if (access.canToggle && input.hasProducts)
    entries.push(
      entry(
        'select',
        'menuPage.more.select',
        'checkbox-multiple-marked-outline'
      )
    );
  if (tier === 'phone' && access.canManage)
    entries.push(
      entry('options', 'menuPage.more.options', 'format-list-checks')
    );
  if (tier !== 'desktop' && access.canManage && !input.multipleMenus)
    entries.push(entry('import', 'menuPage.more.import', 'camera-outline'));
  if (access.canManage)
    entries.push(
      entry('bulkPrices', 'menuPage.more.bulkPrices', 'cash-multiple', {
        plan: planBadge(input.planTier, 1),
      })
    );
  if (access.canManage)
    entries.push(
      entry('stickers', 'menuPage.more.stickers', 'label-outline', {
        plan: planBadge(input.planTier, 2),
      })
    );
  if (access.canManage)
    entries.push(entry('settings', 'menuPage.more.settings', 'cog-outline'));
  if (input.canViewMenu)
    entries.push(entry('viewMenu', 'menuPage.more.viewMenu', 'open-in-new'));
  return entries;
}

//#endregion
